<script setup lang="ts">
import { defineProps, ref, defineEmits } from "vue";

import Pill from "@/components/ui/atoms/Pill.vue";
import PillContainer from "@/components/ui/atoms/PillContainer.vue";

const animate = ref(false);

interface Props {
  text?: string;
}

const emits = defineEmits(["click"]);
defineProps<Props>();

const handleClick = () => {
  animate.value = true;
  setTimeout(() => {
    animate.value = false;
    emits("click");
  }, 240);
};
</script>

<template>
  <PillContainer
    tag="div"
    theme="accent"
    relaxed
    shape="round"
    :class="['button-cta']"
    @click="handleClick()"
  >
    <Pill
      tag="div"
      size="medium"
      theme="dark"
      shape="round"
      :class="['button-cta__arrow-container', { clicked: animate }]"
    >
      <div class="button-cta__arrow" />
    </Pill>
    <div class="pill pill--transparent button-cta__text">
      <span>{{ text }}</span>
    </div>
  </PillContainer>
</template>

<style lang="scss" scoped>
@use "@/styles/settings/vars";

.button-cta {
  position: relative;
  align-items: center;
  cursor: pointer;
  width: max-content;

  &__arrow-container {
    padding: 0.75em 1.5em;
    position: absolute;
    width: 120px;
    margin: 0;
    align-self: center;
    transition: width 0.3s ease-out;
  }

  &__arrow {
    background-image: url("@img/icons/arrow.svg");
    background-repeat: no-repeat;
    background-size: auto 100%;
    background-position: right bottom;
    width: 100%;
    height: 1em;
  }

  &__text {
    margin-left: 140px;
    text-align: left;
  }

  &__text span {
    font-size: 1.5em;
    font-weight: 600;
    line-height: 1.1em;
  }

  &:hover {
    .button-cta__arrow-container {
      width: 140px;
      transition: width 0.3s ease-in-out;
    }
  }

  &:active .button-cta__arrow-container,
  & .button-cta__arrow-container.clicked {
    width: calc(100% - 8px);
    transition: width 0.3s ease-in-out;
  }
}

@media (max-width: 1024px) {
  .button-cta {
    &__arrow-container {
      padding: 0.75em 1.5em;
      width: 100px;
    }

    &__text {
      margin-left: 120px;
    }

    &__text span {
      font-size: 1em;
      font-weight: 600;
      line-height: calc(1em + 4px);
    }

    &:hover {
      .button-cta__arrow-container {
        width: 120px;
      }
    }

    &:active .button-cta__arrow-container,
    & .button-cta__arrow-container.clicked {
      width: calc(100% - 8px);
      transition: width 0.3s ease-in-out;
    }
  }
}

@media (max-width: 600px) {
  .button-cta {
    &__arrow-container {
      padding: 0.75em 1.5em;
      width: 90px;
    }

    &:hover {
      .button-cta__arrow-container {
        width: 110px;
      }
    }

    &__text {
      margin-left: 100px;
    }

    &:active .button-cta__arrow-container,
    & .button-cta__arrow-container.clicked {
      width: calc(100% - 8px);
      transition: width 0.3s ease-in-out;
    }
  }
}
</style>
